import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { getAllCoursesQuery } from './query.get-all.js';
import { duplicateCourseRequest } from './request.duplicate-course.js';
import { createLocalCourse } from './utils/create-local-course.js';

function useDuplicateCourseMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  return useMutation(duplicateCourseMutation(client, notifier, user));
}

/**
 * Updates a course optimisticly and reverts in case anything fails on the
 * backend.
 */
const duplicateCourseMutation = (client, notifier, user) => {
  return {
    mutationKey: ["course-builder/duplicate", user?.id],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({
      __internalId,
      ...courseToDuplicate
    }) => {
      const localDuplicate = createLocalCourse(user, {
        ...courseToDuplicate,
        title: `Kopi af ${courseToDuplicate.title}`
      });
      return duplicateCourseRequest(localDuplicate);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onMutate: async ({
      __internalId,
      ...courseToDuplicate
    }) => {
      const optimisticDuplicate = createLocalCourse(user, {
        ...courseToDuplicate,
        title: `Kopi af ${courseToDuplicate.title}`
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => [...(previous ?? []), optimisticDuplicate]);
      await client.cancelQueries({
        queryKey: getAllCoursesQuery.queryKey({
          user
        })
      });
      return {
        optimisticDuplicate
      };
    },
    onSuccess: result => {
      notifier.notify({
        id: "course-builder.duplicate",
        type: "success",
        body: "Forløbet er blevet kopieret."
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.map(it => it.__internalId === result.__internalId ? result : it));
    },
    onError: (error, _, context) => {
      notifier.notify({
        id: "course-builder.duplicate.error",
        type: "error",
        body: "Forløbet kunne ikke kopieres. Prøv igen om lidt."
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.filter(course => course.__internalId !== context?.optimisticDuplicate.id));
    }
  };
};

export { useDuplicateCourseMutation };
//# sourceMappingURL=mutation.duplicate.js.map
